import { Store, Swords, Handshake, MapPin, Search, X } from 'lucide-react'
import { Card, CardContent } from 'components/ui/card'
import { cn } from 'lib/utils'

/**
 * What kind of nearby retail a card represents.
 *
 * These are all locations that are NOT in our system: they come from POI data
 * and market research rather than from the Location collection, so they have no
 * `_id` of ours and are never editable here.
 */
export type OtherRetailCategory = 'poi' | 'competitor' | 'cotenant'

export interface OtherRetailItem {
	/** Stable id from whichever source supplied the row. */
	id: string
	name: string
	category: OtherRetailCategory
	/** Banner or chain, when the name itself is a store number. */
	brand?: string
	address?: string
	city?: string
	stateProvince?: string
	postalCode?: string
	latitude?: number
	longitude?: number
	/** Straight-line distance from the subject location. */
	distanceMiles?: number
	image?: string | null
	/**
	 * Pin identity for rows with no logo. Every saved row carries both, so the
	 * card and the map marker show the same mark rather than one falling back
	 * to a generic icon while the other shows initials.
	 */
	monogram?: string
	monogramColor?: string
	/** Where the row came from, e.g. the POI provider. */
	source?: string
}

const CATEGORY_META: Record<
	OtherRetailCategory,
	{ label: string; icon: typeof Store; badge: string; tile: string }
> = {
	poi: {
		label: 'Retail POI',
		icon: Store,
		badge: 'bg-secondary text-secondary-foreground',
		tile: 'bg-muted text-muted-foreground',
	},
	competitor: {
		label: 'Competitor',
		icon: Swords,
		badge: 'bg-destructive/10 text-destructive',
		tile: 'bg-destructive/10 text-destructive',
	},
	cotenant: {
		label: 'Cotenant',
		icon: Handshake,
		badge: 'bg-success/10 text-success',
		tile: 'bg-success/10 text-success',
	},
}

interface OtherRetailPanelProps {
	items?: OtherRetailItem[]
	isLoading?: boolean
	/** Fired by a card click, so the caller can focus the map or open detail. */
	onSelectItem?: (item: OtherRetailItem) => void
	/** Opens the scan dialog. The Scan button only renders when supplied. */
	onScan?: () => void
	/** Removes a saved retailer. The X only renders when supplied. */
	onRemoveItem?: (item: OtherRetailItem) => void
	/**
	 * Set when a trade-area ring is narrowing the list, so the empty state can
	 * say "nothing within 1 mile" rather than implying nothing was ever saved.
	 */
	ringLimit?: number | null
	className?: string
}

/**
 * Other Retail — nearby retail that is not in our system.
 *
 * Sits under the location cards in the map panel's left column and holds three
 * kinds of row: retail POIs, competitors, and proximate cotenants. None of them
 * are Location records, so they are read-only here and carry their own source
 * rather than one of our ids.
 *
 * Renders its empty state rather than hiding when there is nothing to show, so
 * the container is discoverable before any data is wired to it.
 */
const OtherRetailPanel = ({
	items = [],
	isLoading = false,
	onSelectItem,
	onScan,
	onRemoveItem,
	ringLimit = null,
	className,
}: OtherRetailPanelProps) => (
	<section className={cn('pt-2', className)} data-testid="other-retail-panel">
		<div className="mb-2 flex items-center gap-2 border-t border-gray-200 pt-3">
			<h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
				Other Retail
			</h3>
			{items.length > 0 && (
				<span className="text-xs tabular-nums text-gray-400">{items.length}</span>
			)}
			{onScan && (
				<button
					type="button"
					onClick={onScan}
					data-testid="other-retail-scan"
					className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[#0080fe] transition-colors hover:bg-[#0080fe]/10"
				>
					<Search className="h-3 w-3" />
					Scan
				</button>
			)}
		</div>

		{isLoading && <p className="m-0 text-xs italic text-gray-400">Loading nearby retail...</p>}

		{!isLoading && items.length === 0 && (
			<p className="m-0 text-xs leading-relaxed text-gray-400">
				{ringLimit != null
					? `No saved retailers within ${ringLimit} mile${ringLimit === 1 ? '' : 's'}. Widen the ring selection to see more.`
					: onScan
						? 'Scan to find nearby retail POIs, competitors and cotenants. These are locations outside your portfolio, so they are for context only.'
						: 'Nearby retail POIs, competitors and cotenants will appear here. These are locations outside your portfolio, so they are for context only.'}
			</p>
		)}

		<div className="space-y-2">
			{items.map(item => {
				const meta = CATEGORY_META[item.category] ?? CATEGORY_META.poi
				const Icon = meta.icon
				const cityStateZip = [
					[item.city, item.stateProvince].filter(Boolean).join(', '),
					item.postalCode,
				]
					.filter(Boolean)
					.join(' ')

				return (
					<Card
						key={item.id}
						data-testid="other-retail-card"
						className={cn(
							'overflow-hidden transition-shadow',
							onSelectItem && 'cursor-pointer hover:shadow-sm',
						)}
						onClick={onSelectItem ? () => onSelectItem(item) : undefined}
					>
						<CardContent className="p-4">
							<div className="flex items-start gap-3">
								<div
									className={cn(
										'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg',
										!item.image && !item.monogram && meta.tile,
									)}
									style={
										!item.image && item.monogram
											? { background: item.monogramColor || '#4A5568' }
											: undefined
									}
								>
									{item.image ? (
										<img
											src={item.image}
											alt=""
											loading="lazy"
											className="h-10 w-10 object-contain p-0.5"
										/>
									) : item.monogram ? (
										<span className="text-sm font-semibold text-white">{item.monogram}</span>
									) : (
										<Icon className="h-4 w-4" />
									)}
								</div>

								<div className="min-w-0 flex-1">
									<h4 className="truncate text-sm font-semibold text-gray-900" title={item.name}>
										{item.name}
									</h4>
									{item.brand && (
										<p className="truncate text-xs text-gray-500" title={item.brand}>
											{item.brand}
										</p>
									)}
								</div>

								{onRemoveItem && (
									<button
										type="button"
										aria-label={`Remove ${item.name}`}
										onClick={(e) => {
											// The card itself is clickable, so a remove that
											// also focused the map would be a confusing exit.
											e.stopPropagation()
											onRemoveItem(item)
										}}
										className="-mr-1 -mt-1 shrink-0 rounded p-1 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-600"
									>
										<X className="h-3.5 w-3.5" />
									</button>
								)}
							</div>

							<div className="mt-2 flex flex-wrap items-center gap-1.5">
								<span
									className={cn(
										'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
										meta.badge,
									)}
								>
									{meta.label}
								</span>
								{item.distanceMiles != null && (
									<span className="inline-flex items-center gap-0.5 text-[10px] text-gray-400">
										<MapPin className="h-3 w-3" />
										{item.distanceMiles.toFixed(1)} mi
									</span>
								)}
							</div>

							{(item.address || cityStateZip) && (
								<div className="mt-2 text-xs text-gray-600">
									{item.address && (
										<span className="block truncate" title={item.address}>
											{item.address}
										</span>
									)}
									{cityStateZip && (
										<span className="block truncate text-gray-400" title={cityStateZip}>
											{cityStateZip}
										</span>
									)}
								</div>
							)}
						</CardContent>
					</Card>
				)
			})}
		</div>
	</section>
)

export default OtherRetailPanel
